import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';

// A Procedures answer is 2D — a list of entries, each with its own sub-fields — so the generic
// (1D) required check can't reach inside it. This is the special mechanism that descends into an
// entry's sub-fields and returns errors keyed by [entryIndex][subFieldKey], the coordinates the
// UI needs to mark the right box. Required-ness is data-driven off the served `required` flags,
// mirroring the backend's own per-entry validation (the backend stays the safety net at submit).

export type Entry = Record<string, AnswerTypes>;

export type RepeatingGroupErrors = Record<number, Record<string, string>>;

interface SubFieldDef {
  required?: boolean;
  // Served for the quantity field; the max is owned by the backend and exposed on the payload.
  maximum?: number;
}

interface ValidatableProperties {
  order: string[];
  properties: Record<string, SubFieldDef>;
}

export const REQUIRED_MESSAGE = 'This field is required.';

const isEmpty = (value: AnswerTypes | undefined): boolean =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

export const validateRepeatingGroupEntries = (
  entries: Entry[],
  properties: ValidatableProperties
): RepeatingGroupErrors => {
  const errors: RepeatingGroupErrors = {};

  entries.forEach((entry, index) => {
    const entryErrors: Record<string, string> = {};

    properties.order.forEach((subKey) => {
      const def = properties.properties[subKey];
      const value = entry[subKey];

      if (def?.required && isEmpty(value)) {
        entryErrors[subKey] = REQUIRED_MESSAGE;
        return;
      }

      if (def?.maximum != null && typeof value === 'number' && value > def.maximum) {
        entryErrors[subKey] = `Must be ${def.maximum} or fewer.`;
      }
    });

    if (Object.keys(entryErrors).length > 0) {
      errors[index] = entryErrors;
    }
  });

  return errors;
};

export const hasRepeatingGroupErrors = (errors: RepeatingGroupErrors): boolean => Object.keys(errors).length > 0;
